import { withAlpha } from "@/lib/color";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";
import PlayerAvatar from "@/components/PlayerAvatar";
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
    closestCenter,
} from "@dnd-kit/core";

const SCHEMES = ["4-3-3", "4-4-2", "3-5-2", "5-3-2", "4-2-3-1", "4-3-1-2", "3-4-3"];

/**
 * TacticalBoard — Visual pitch with 11 starters, substitutes and drag & drop.
 *
 * Drag sources:
 *   - Starter avatar on the pitch (draggable id = `pitch:<slotId>`)
 *   - Bench/roster player (draggable id = `bench:<playerKey>`)
 *
 * Drop targets:
 *   - Pitch slots (droppable id = `slot:<slotId>`)
 *
 * Dropping a bench player on a slot → starter change (with auto-swap if needed).
 * Dropping a starter on another slot → swap of the two starters.
 */
export default function TacticalBoard({ teamId, canEdit = false, roster = [], accent = "#2BB6FF" }) {
    const [data, setData] = useState(null);
    const [editingSlot, setEditingSlot] = useState(null);
    const [editingSubSlot, setEditingSubSlot] = useState(null);
    const [editingRoleSlot, setEditingRoleSlot] = useState(null);
    const [activeDrag, setActiveDrag] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const pitchRef = useRef(null);

    // Sensors with activation distance to avoid accidental drags
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
        useSensor(KeyboardSensor),
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/teams/${teamId}/formation`);
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.detail || e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [teamId]);

    const startersByKey = useMemo(() => {
        const m = new Map();
        if (data) for (const s of data.slots) {
            const k = s.starter?.pes_id ?? s.starter?.legacy_player_id;
            if (k != null) m.set(String(k), s.id);
        }
        return m;
    }, [data]);

    // Pool for the bench = main roster players NOT currently starters, sorted by VG desc
    const benchPlayers = useMemo(() => {
        const playing = new Set(startersByKey.keys());
        return roster
            .filter((p) => !p.is_ugly && !p.is_loaned_out)
            .filter((p) => !playing.has(String(p.pes_id ?? p.legacy_player_id)))
            .sort((a, b) => (b.vg || 0) - (a.vg || 0));
    }, [roster, startersByKey]);

    const handleSchemeChange = async (newScheme) => {
        if (!canEdit || saving || newScheme === data?.scheme) return;
        setSaving(true);
        setErr("");
        try {
            // Non-destructive switch: keeps each scheme's saved layout/roles
            const { data: updated } = await api.patch(`/teams/${teamId}/formation/active`, {
                scheme: newScheme,
            });
            setData(updated);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    const applyStartersChange = async (changes, subChanges = [], extra = {}) => {
        if (!canEdit || !data || saving) return;
        setSaving(true);
        setErr("");
        try {
            const starters = {};
            const substitutes = {};
            const slotOverrides = { ...(data.slot_overrides || {}) };
            const roles = { ...(data.roles || {}) };
            for (const s of data.slots) {
                const cur = s.starter?.pes_id ?? s.starter?.legacy_player_id;
                if (cur != null) starters[s.id] = cur;
                if (s.substitute_is_manual) {
                    const sub = s.substitute?.pes_id ?? s.substitute?.legacy_player_id;
                    if (sub != null) substitutes[s.id] = sub;
                }
            }
            for (const { slotId, newKey } of changes) {
                if (newKey == null) {
                    delete starters[slotId];
                } else {
                    starters[slotId] = newKey;
                }
            }
            for (const { slotId, newSubKey } of subChanges) {
                if (newSubKey == null) {
                    delete substitutes[slotId];
                } else {
                    substitutes[slotId] = newSubKey;
                }
            }
            // Merge in any positional override changes
            if (extra.slotOverridePatch) {
                for (const [sid, pos] of Object.entries(extra.slotOverridePatch)) {
                    if (pos == null) {
                        delete slotOverrides[sid];
                    } else {
                        slotOverrides[sid] = pos;
                    }
                }
            }
            // Merge in any tactical role changes
            if (extra.rolePatch) {
                for (const [sid, r] of Object.entries(extra.rolePatch)) {
                    if (r == null) {
                        delete roles[sid];
                    } else {
                        roles[sid] = r;
                    }
                }
            }
            const captain = extra.captain !== undefined ? extra.captain : (data.captain ?? null);

            const { data: updated } = await api.put(`/teams/${teamId}/formation`, {
                scheme: data.scheme,
                starters,
                substitutes,
                slot_overrides: slotOverrides,
                roles,
                captain,
            });
            setData(updated);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSetCaptain = async (playerKey) => {
        // Toggle: if already captain, clear; otherwise set
        const newCaptain = data?.captain === playerKey ? null : playerKey;
        await applyStartersChange([], [], { captain: newCaptain });
    };

    // Picker-based starter change (used by the modal fallback)
    const handlePickerChange = async (slotId, newKey) => {
        if (!data) return;
        const previousKey = data.slots.find((s) => s.id === slotId)?.starter?.pes_id
            ?? data.slots.find((s) => s.id === slotId)?.starter?.legacy_player_id;
        const fromSlot = startersByKey.get(String(newKey));
        const changes = [{ slotId, newKey }];
        if (fromSlot && fromSlot !== slotId) {
            // SWAP: previous starter of slotId goes to the source slot
            changes.push({ slotId: fromSlot, newKey: previousKey ?? null });
        }
        await applyStartersChange(changes);
        setEditingSlot(null);
    };

    const handleSubPick = async (slotId, newSubKey) => {
        if (!data) return;
        await applyStartersChange([], [{ slotId, newSubKey }]);
        setEditingSubSlot(null);
    };

    const handleSubAuto = async (slotId) => {
        await applyStartersChange([], [{ slotId, newSubKey: null }]);
        setEditingSubSlot(null);
    };

    const handleRolePick = async (slotId, role) => {
        await applyStartersChange([], [], { rolePatch: { [slotId]: role } });
        setEditingRoleSlot(null);
    };


    // Drag & drop handlers
    const handleDragStart = (event) => {
        setActiveDrag(event.active);
    };

    const handleDragEnd = async (event) => {
        setActiveDrag(null);
        const { active, over, delta } = event;
        if (!canEdit || !data) return;

        const activeId = String(active.id);

        // Case A: dropped on a pitch slot → existing swap / placement logic
        if (over) {
            const overId = String(over.id);
            if (overId.startsWith("slot:")) {
                const targetSlotId = overId.slice(5);
                if (activeId.startsWith("bench:")) {
                    const newKey = active.data.current?.playerKey;
                    if (newKey == null) return;
                    const previousKey = data.slots.find((s) => s.id === targetSlotId)?.starter?.pes_id
                        ?? data.slots.find((s) => s.id === targetSlotId)?.starter?.legacy_player_id;
                    const changes = [{ slotId: targetSlotId, newKey }];
                    const fromSlot = startersByKey.get(String(newKey));
                    if (fromSlot && fromSlot !== targetSlotId) {
                        changes.push({ slotId: fromSlot, newKey: previousKey ?? null });
                    }
                    await applyStartersChange(changes);
                    return;
                }
                if (activeId.startsWith("pitch:")) {
                    const sourceSlotId = activeId.slice(6);
                    // Different slot → swap; same slot → fall through to free-positioning
                    if (sourceSlotId !== targetSlotId) {
                        const sourceStarter = data.slots.find((s) => s.id === sourceSlotId)?.starter;
                        const targetStarter = data.slots.find((s) => s.id === targetSlotId)?.starter;
                        const sourceKey = sourceStarter?.pes_id ?? sourceStarter?.legacy_player_id;
                        const targetKey = targetStarter?.pes_id ?? targetStarter?.legacy_player_id;
                        const changes = [
                            { slotId: targetSlotId, newKey: sourceKey ?? null },
                            { slotId: sourceSlotId, newKey: targetKey ?? null },
                        ];
                        await applyStartersChange(changes);
                        return;
                    }
                }
            }
        }

        // Case B: free repositioning — starter dragged but not dropped on a slot.
        // Compute new x/y in percentage of pitch and persist as slot_overrides.
        if (activeId.startsWith("pitch:") && delta && pitchRef.current) {
            const sourceSlotId = activeId.slice(6);
            const slot = data.slots.find((s) => s.id === sourceSlotId);
            if (!slot) return;
            const rect = pitchRef.current.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return;
            // Ignore tiny drags (treat as click)
            if (Math.abs(delta.x) < 4 && Math.abs(delta.y) < 4) return;
            const dxPct = (delta.x / rect.width) * 100;
            const dyPct = (delta.y / rect.height) * 100;
            const newX = Math.max(5, Math.min(95, (slot.x || 50) + dxPct));
            const newY = Math.max(4, Math.min(96, (slot.y || 50) + dyPct));
            await applyStartersChange([], [], {
                slotOverridePatch: { [sourceSlotId]: { x: newX, y: newY } },
            });
        }
    };

    if (err) {
        return (
            <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3" data-testid="tactical-error">
                {err}
            </div>
        );
    }
    if (!data) {
        return <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO CAMPO…</div>;
    }

    const eligible = roster
        .filter((p) => !p.is_ugly && !p.is_loaned_out)
        .sort((a, b) => (b.vg || 0) - (a.vg || 0));

    const activePayload = activeDrag?.data?.current;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <section className="op-card overflow-hidden" data-testid="tactical-board">
                {/* Top bar */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-t22252a flex-wrap">
                    <div className="flex items-center gap-3">
                        <span className="op-label text-t2bb6ff">// MODULO</span>
                        {canEdit ? (
                            <div className="flex gap-1 flex-wrap" data-testid="scheme-selector">
                                {SCHEMES.map((s) => {
                                    const active = s === data.scheme;
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => handleSchemeChange(s)}
                                            disabled={saving}
                                            className="op-mono text-[11px] px-2.5 py-1 border transition-colors disabled:opacity-50"
                                            style={{
                                                background: active ? accent : "transparent",
                                                color: active ? "#000" : accent,
                                                borderColor: withAlpha(accent, "88"),
                                            }}
                                            data-testid={`scheme-${s}`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <span className="op-heading text-2xl" style={{ color: accent }}>{data.scheme}</span>
                        )}
                    </div>
                    <div className="op-mono text-[10px] text-t8f95a3 uppercase flex items-center gap-3">
                        {canEdit && Object.keys(data.slot_overrides || {}).length > 0 && (
                            <button
                                type="button"
                                onClick={async () => {
                                    if (saving) return;
                                    setSaving(true);
                                    setErr("");
                                    try {
                                        const { data: updated } = await api.put(`/teams/${teamId}/formation`, {
                                            scheme: data.scheme,
                                            starters: Object.fromEntries(
                                                data.slots
                                                    .map((s) => [s.id, s.starter?.pes_id ?? s.starter?.legacy_player_id])
                                                    .filter(([, v]) => v != null),
                                            ),
                                            substitutes: Object.fromEntries(
                                                data.slots
                                                    .filter((s) => s.substitute_is_manual)
                                                    .map((s) => [s.id, s.substitute?.pes_id ?? s.substitute?.legacy_player_id])
                                                    .filter(([, v]) => v != null),
                                            ),
                                            slot_overrides: {},
                                            captain: data.captain ?? null,
                                        });
                                        setData(updated);
                                    } catch (e) {
                                        setErr(e.response?.data?.detail || e.message);
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                                className="op-mono text-[10px] px-2 py-0.5 border border-t8f95a3 text-t8f95a3 hover:text-tffffff hover:border-tffffff"
                                data-testid="reset-positions"
                                title="Riporta tutti i giocatori alle posizioni del modulo"
                            >
                                ↺ Reset posizioni
                            </button>
                        )}
                        <span>{canEdit ? "Trascina per riposizionare · su un altro per scambiare" : "Read-only"}</span>
                    </div>
                </div>

                {/* Pitch */}
                <div
                    ref={pitchRef}
                    className="relative w-full"
                    style={{
                        aspectRatio: "100 / 130",
                        background: "linear-gradient(180deg, #0a2e15 0%, #0f4220 50%, #0a2e15 100%)",
                        maxWidth: "560px",
                        margin: "0 auto",
                    }}
                    data-testid="pitch"
                >
                    <PitchLines />

                    {data.slots.map((slot) => {
                        const playerKey = slot.starter?.pes_id ?? slot.starter?.legacy_player_id;
                        const isCaptain = playerKey != null && playerKey === data.captain;
                        const captainSet = data.captain != null;
                        return (
                            <PitchSlot
                                key={slot.id}
                                slot={slot}
                                accent={accent}
                                editable={canEdit}
                                isCaptain={isCaptain}
                                captainSet={captainSet}
                                onTap={() => canEdit && setEditingSlot(slot.id)}
                                onTapSub={() => canEdit && setEditingSubSlot(slot.id)}
                                onTapRole={() => canEdit && setEditingRoleSlot(slot.id)}
                                onToggleCaptain={() => canEdit && playerKey != null && handleSetCaptain(playerKey)}
                            />
                        );
                    })}
                </div>

                {/* Bench / panchina visible always when canEdit */}
                {canEdit && (
                    <Bench players={benchPlayers} accent={accent} />
                )}

                {/* Slot picker modal (still available as fallback for taps) */}
                {editingSlot && canEdit && (
                    <SlotPicker
                        slot={data.slots.find((s) => s.id === editingSlot)}
                        eligible={eligible}
                        startersByKey={startersByKey}
                        onPick={(key) => handlePickerChange(editingSlot, key)}
                        onClose={() => setEditingSlot(null)}
                    />
                )}

                {/* Substitute picker modal */}
                {editingSubSlot && canEdit && (
                    <SubstitutePicker
                        slot={data.slots.find((s) => s.id === editingSubSlot)}
                        eligible={eligible}
                        startersByKey={startersByKey}
                        onPick={(key) => handleSubPick(editingSubSlot, key)}
                        onAuto={() => handleSubAuto(editingSubSlot)}
                        onClose={() => setEditingSubSlot(null)}
                    />
                )}

                {/* Tactical role picker modal */}
                {editingRoleSlot && canEdit && (
                    <RolePicker
                        slot={data.slots.find((s) => s.id === editingRoleSlot)}
                        onPick={(role) => handleRolePick(editingRoleSlot, role)}
                        onClose={() => setEditingRoleSlot(null)}
                    />
                )}
            </section>

            {/* Drag overlay — floating avatar during drag */}
            <DragOverlay>
                {activePayload && activePayload.player ? (
                    <DragGhost player={activePayload.player} accent={accent} />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

function PitchLines() {
    return (
        <svg viewBox="0 0 100 130" preserveAspectRatio="none"
             className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.45 }}>
            <rect x="2" y="2" width="96" height="126" fill="none" stroke="#fff" strokeWidth="0.4" />
            <line x1="2" y1="65" x2="98" y2="65" stroke="#fff" strokeWidth="0.4" />
            <circle cx="50" cy="65" r="9" fill="none" stroke="#fff" strokeWidth="0.4" />
            <circle cx="50" cy="65" r="0.6" fill="#fff" />
            <rect x="22" y="2" width="56" height="14" fill="none" stroke="#fff" strokeWidth="0.4" />
            <rect x="36" y="2" width="28" height="5" fill="none" stroke="#fff" strokeWidth="0.4" />
            <rect x="22" y="114" width="56" height="14" fill="none" stroke="#fff" strokeWidth="0.4" />
            <rect x="36" y="123" width="28" height="5" fill="none" stroke="#fff" strokeWidth="0.4" />
        </svg>
    );
}

function PitchSlot({ slot, accent, editable, isCaptain, captainSet, onTap, onTapSub, onTapRole, onToggleCaptain }) {
    const top = `${(slot.y / 100) * 100}%`;
    const left = `${slot.x}%`;
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `slot:${slot.id}`,
        disabled: !editable,
    });

    return (
        <div
            ref={setDropRef}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 w-[58px] sm:w-[80px] md:w-[92px]"
            style={{
                top, left,
                outline: isOver ? `2px dashed ${accent}` : "none",
                outlineOffset: "8px",
                borderRadius: "8px",
            }}
            data-testid={`slot-${slot.id}`}
        >
            <DraggableStarter
                slot={slot}
                accent={accent}
                editable={editable}
                isCaptain={isCaptain}
                captainSet={captainSet}
                onTap={onTap}
                onTapSub={onTapSub}
                onTapRole={onTapRole}
                onToggleCaptain={onToggleCaptain}
            />
        </div>
    );
}

function DraggableStarter({ slot, accent, editable, isCaptain, captainSet, onTap, onTapSub, onTapRole, onToggleCaptain }) {
    const s = slot.starter;
    const sub = slot.substitute;
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `pitch:${slot.id}`,
        disabled: !editable || !s,
        data: { player: s, sourceSlotId: slot.id },
    });
    return (
        <>
            <div
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                className={`relative w-[38px] h-[38px] sm:w-[44px] sm:h-[44px] md:w-[48px] md:h-[48px] ${editable && s ? "cursor-grab active:cursor-grabbing" : ""}`}
                style={{ opacity: isDragging ? 0.3 : 1, touchAction: "none" }}
                onClick={editable ? onTap : undefined}
            >
                <div
                    className="rounded-full overflow-hidden border-2 shadow-lg bg-t0d0f12 w-full h-full"
                    style={{
                        borderColor: isCaptain ? "#FFD700" : accent,
                        boxShadow: isCaptain ? "0 0 12px rgba(255,215,0,0.55)" : undefined,
                    }}
                >
                    {s ? (
                        <PlayerAvatar tmImage={s.tm_image} pesImage={s.pes_image_url} name={s.name} size={48} />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center op-mono text-[10px] text-t8f95a3">
                            {trPositionShort(slot.label)}
                        </div>
                    )}
                </div>
                {isCaptain && (
                    <span
                        className="absolute -top-1.5 -left-1.5 op-mono text-[10px] sm:text-[11px] font-bold w-[18px] h-[18px] sm:w-[20px] sm:h-[20px] flex items-center justify-center rounded-full border-2 bg-black text-tffd700 leading-none"
                        style={{ borderColor: "#FFD700", textShadow: "0 0 4px rgba(255,215,0,0.7)" }}
                        title="Capitano"
                        data-testid={`captain-badge-${slot.id}`}
                    >
                        C
                    </span>
                )}
                {s?.vg != null && (
                    <span className="absolute -bottom-1.5 -right-1 op-mono text-[9px] sm:text-[10px] font-bold px-1 sm:px-1.5 py-0.5 rounded-sm bg-black border leading-none"
                          style={{ borderColor: accent, color: accent }}>
                        {s.vg}
                    </span>
                )}
            </div>
            <div className="op-mono text-[8px] sm:text-[9px] md:text-[10px] text-tffffff font-bold text-center leading-[1.1] drop-shadow-[0_1px_2px_rgba(0,0,0,0.95)] break-words"
                 title={s?.name || slot.label}>
                {s?.name || "—"}
            </div>
            {slot.role && (
                editable ? (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onTapRole?.(); }}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="op-mono text-[7px] sm:text-[8px] uppercase leading-none px-1.5 py-0.5 border rounded-sm transition-colors hover:bg-tffffff/10"
                        style={{ color: accent, borderColor: withAlpha(accent, "66") }}
                        title="Imposta ruolo tattico"
                        data-testid={`role-chip-${slot.id}`}
                    >
                        {trPositionShort(slot.role)}
                    </button>
                ) : (
                    <span
                        className="op-mono text-[7px] sm:text-[8px] uppercase leading-none px-1.5 py-0.5 border rounded-sm"
                        style={{ color: accent, borderColor: withAlpha(accent, "66") }}
                        data-testid={`role-chip-${slot.id}`}
                    >
                        {trPositionShort(slot.role)}
                    </span>
                )
            )}
            {editable && s && (!captainSet || isCaptain) && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleCaptain?.(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className={`op-mono text-[7px] sm:text-[8px] uppercase leading-none px-1.5 py-0.5 border rounded-sm transition-colors ${
                        isCaptain
                            ? "bg-tffd700 text-black border-tffd700"
                            : "text-tffd700 border-tffd700/40 hover:bg-tffd700/20"
                    }`}
                    title={isCaptain ? "Rimuovi capitano" : "Imposta come capitano"}
                    data-testid={`captain-toggle-${slot.id}`}
                >
                    {isCaptain ? "★ CAPITANO" : "+ CAPITANO"}
                </button>
            )}
            {/* Substitute label — clickable to change manually */}
            <button
                type="button"
                onClick={editable ? (e) => { e.stopPropagation(); onTapSub?.(); } : undefined}
                onPointerDown={editable ? (e) => e.stopPropagation() : undefined}
                disabled={!editable}
                className={`op-mono text-[7px] sm:text-[9px] italic text-center leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] break-words max-w-full ${editable ? "cursor-pointer hover:text-tffffff" : "cursor-default"}`}
                style={{ color: slot.substitute_is_manual ? "#E8FF00" : "#c9ccd3" }}
                title={sub ? `Riserva: ${sub.name}${slot.substitute_is_manual ? " (scelta manualmente)" : " (suggerita)"} — clicca per cambiare` : "Imposta riserva"}
                data-testid={`sub-${slot.id}`}
            >
                ({sub?.name || "—"})
            </button>
        </>
    );
}

function Bench({ players, accent }) {
    return (
        <div className="border-t border-t22252a p-3" data-testid="bench">
            <div className="op-label text-t8f95a3 mb-2">
                // PANCHINA — Trascina sul campo
                {players.length > 0 && (
                    <span className="ml-2 text-t5c6370">({players.length})</span>
                )}
            </div>
            {/* Mobile: flex-wrap multi-riga (no scroll orizzontale, conflitto con touchAction drag).
                Desktop ≥sm: scroll orizzontale tradizionale. */}
            <div
                className="flex flex-wrap sm:flex-nowrap gap-2 sm:overflow-x-auto pb-2"
                style={{ scrollSnapType: "x mandatory" }}
            >
                {players.length === 0 && (
                    <div className="op-mono text-[11px] text-t8f95a3 italic">Tutti in campo.</div>
                )}
                {players.map((p) => (
                    <BenchItem key={p.pes_id ?? p.legacy_player_id} player={p} accent={accent} />
                ))}
            </div>
        </div>
    );
}

function BenchItem({ player, accent }) {
    const playerKey = player.pes_id ?? player.legacy_player_id;
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `bench:${playerKey}`,
        data: { player, playerKey },
    });
    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className="flex flex-col items-center gap-1 w-[56px] flex-shrink-0 cursor-grab active:cursor-grabbing"
            style={{ opacity: isDragging ? 0.3 : 1, scrollSnapAlign: "start", touchAction: "none" }}
            data-testid={`bench-${playerKey}`}
        >
            <div className="relative w-9 h-9">
                <div className="rounded-full overflow-hidden border bg-t0d0f12 w-full h-full" style={{ borderColor: withAlpha(accent, "AA") }}>
                    <PlayerAvatar tmImage={player.tm_image} pesImage={player.pes_image_url} name={player.name} size={36} />
                </div>
                {player.vg != null && (
                    <span className="absolute -bottom-1 -right-1 op-mono text-[8px] font-bold px-1 rounded-sm bg-black border leading-none"
                          style={{ borderColor: accent, color: accent }}>
                        {player.vg}
                    </span>
                )}
            </div>
            <div className="op-mono text-[8px] text-tffffff text-center leading-tight break-words max-w-full" title={player.name}>
                {player.name.split(" ").slice(-1)[0]}
            </div>
            <div className="op-mono text-[7px] text-t8f95a3">{trPositionShort(player.position)}</div>
        </div>
    );
}

function DragGhost({ player, accent }) {
    return (
        <div className="flex flex-col items-center gap-1 pointer-events-none">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 shadow-2xl bg-t0d0f12" style={{ borderColor: accent }}>
                <PlayerAvatar tmImage={player.tm_image} pesImage={player.pes_image_url} name={player.name} size={48} />
            </div>
            <div className="op-mono text-[10px] text-tffffff font-bold text-center bg-black/70 px-1 py-0.5">{player.name}</div>
        </div>
    );
}

function SlotPicker({ slot, eligible, startersByKey, onPick, onClose }) {
    const accepted = new Set(slot.pos);
    const match = eligible.filter((p) => accepted.has(p.position));
    const others = eligible.filter((p) => !accepted.has(p.position));
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="slot-picker">
            <div className="op-card max-w-md w-full max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="op-label text-t2bb6ff">// SLOT {slot.id}</div>
                        <div className="op-heading text-xl text-tffffff">{trPositionShort(slot.label)} · {slot.pos.map(trPositionShort).join(" / ")}</div>
                    </div>
                    <button onClick={onClose} className="op-mono text-t8f95a3 hover:text-tffffff" data-testid="slot-picker-close">✕</button>
                </div>
                <PickerList title="Ruolo compatibile" players={match} startersByKey={startersByKey} onPick={onPick} />
                {others.length > 0 && (
                    <details className="mt-4">
                        <summary className="op-label text-t8f95a3 cursor-pointer">Altri giocatori in rosa ({others.length})</summary>
                        <PickerList players={others} startersByKey={startersByKey} onPick={onPick} />
                    </details>
                )}
            </div>
        </div>
    );
}

function PickerList({ title, players, startersByKey, onPick }) {
    return (
        <div>
            {title && <div className="op-label text-[10px] mb-2">{title}</div>}
            <div className="space-y-1">
                {players.map((p) => {
                    const k = p.pes_id ?? p.legacy_player_id;
                    const isUsed = startersByKey.has(String(k));
                    return (
                        <button key={k} onClick={() => onPick(k)}
                            className="w-full flex items-center gap-2 p-2 border border-t22252a hover:border-tffffff transition-colors text-left"
                            data-testid={`pick-${k}`}>
                            <PlayerAvatar tmImage={p.tm_image} pesImage={p.pes_image_url} name={p.name} size={28} />
                            <div className="flex-1 min-w-0">
                                <div className="text-tffffff text-sm truncate">{p.name}</div>
                                <div className="op-mono text-[10px] text-t8f95a3">{trPositionShort(p.position)}</div>
                            </div>
                            <div className="op-mono text-sm text-tffffff font-bold">{p.vg ?? "—"}</div>
                            {isUsed && (
                                <span className="op-mono text-[9px] text-te8ff00 uppercase">↔ SWAP</span>
                            )}
                        </button>
                    );
                })}
                {players.length === 0 && (
                    <div className="op-mono text-xs text-t8f95a3 italic py-2">Nessun giocatore disponibile.</div>
                )}
            </div>
        </div>
    );
}


function SubstitutePicker({ slot, eligible, startersByKey, onPick, onAuto, onClose }) {
    const accepted = new Set(slot.pos);
    const starterKey = slot.starter?.pes_id ?? slot.starter?.legacy_player_id;
    // Eligible substitutes: NOT the current starter, NOT another starter
    const candidates = eligible.filter((p) => {
        const k = p.pes_id ?? p.legacy_player_id;
        return k !== starterKey && !startersByKey.has(String(k));
    });
    const match = candidates.filter((p) => accepted.has(p.position));
    const others = candidates.filter((p) => !accepted.has(p.position));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="sub-picker">
            <div className="op-card max-w-md w-full max-h-[85vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="op-label text-te8ff00">// RISERVA SLOT {slot.id}</div>
                        <div className="op-heading text-xl text-tffffff">{trPositionShort(slot.label)} · {slot.pos.map(trPositionShort).join(" / ")}</div>
                        {slot.starter && (
                            <div className="op-mono text-[10px] text-t8f95a3 mt-1">Titolare: {slot.starter.name}</div>
                        )}
                    </div>
                    <button onClick={onClose} className="op-mono text-t8f95a3 hover:text-tffffff" data-testid="sub-picker-close">✕</button>
                </div>
                <button
                    onClick={onAuto}
                    className="w-full op-mono text-[11px] py-2 mb-3 border border-t22252a text-t8f95a3 hover:text-tffffff hover:border-tffffff"
                    data-testid="sub-auto"
                >
                    ↺ Suggerimento automatico
                </button>
                <PickerList title="Ruolo compatibile" players={match} startersByKey={new Map()} onPick={onPick} />
                {others.length > 0 && (
                    <details className="mt-3">
                        <summary className="op-label text-t8f95a3 cursor-pointer">Altri giocatori dalla panchina ({others.length})</summary>
                        <PickerList players={others} startersByKey={new Map()} onPick={onPick} />
                    </details>
                )}
            </div>
        </div>
    );
}

function RolePicker({ slot, onPick, onClose }) {
    if (!slot) return null;
    const options = slot.role_options || slot.pos || [];
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="role-picker">
            <div className="op-card max-w-sm w-full p-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="op-label text-t2bb6ff">// RUOLO TATTICO · {slot.id}</div>
                        <div className="op-heading text-xl text-tffffff">{trPositionShort(slot.label)}</div>
                        {slot.starter && (
                            <div className="op-mono text-[10px] text-t8f95a3 mt-1">{slot.starter.name}</div>
                        )}
                    </div>
                    <button onClick={onClose} className="op-mono text-t8f95a3 hover:text-tffffff" data-testid="role-picker-close">✕</button>
                </div>
                <div className="op-label text-[10px] mb-2">Seleziona il ruolo per questa posizione</div>
                <div className="grid grid-cols-3 gap-2">
                    {options.map((r) => {
                        const active = r === slot.role;
                        return (
                            <button
                                key={r}
                                onClick={() => onPick(r)}
                                className="op-mono text-sm py-2 border transition-colors"
                                style={{
                                    background: active ? "#2BB6FF" : "transparent",
                                    color: active ? "#000" : "#2BB6FF",
                                    borderColor: "#2BB6FF88",
                                }}
                                data-testid={`role-option-${r}`}
                            >
                                {r}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
